import Papa from 'papaparse';

export const FLAG_IMPORT_HEADERS = Object.freeze(['user_id', 'flag_code', 'flag_name']);
export const FLAG_IMPORT_MAX_BYTES = 1024 * 1024;
export const FLAG_IMPORT_MAX_ROWS = 1000;
export const FLAG_IMPORT_TEMPLATE = [
  'user_id,flag_code,flag_name',
  '000123,PAYMENT-HOLD,Payment outstanding',
  '000456,REVIEW,Account review required',
  '000789,PAYMENT-HOLD,Payment outstanding',
].join('\r\n');

const value = (item) => typeof item === 'string' ? item.trim() : `${item === undefined || item === null ? '' : item}`.trim();
const code = (item) => value(item).toUpperCase();
const issue = (rowNumber, message) => ({ rowNumber, message });

export const parseFlagImportFile = (file) => new Promise((resolve, reject) => {
  Papa.parse(file, {
    skipEmptyLines: 'greedy',
    complete: (result) => resolve(result),
    error: reject,
  });
});

export const validateFlagImportCsv = (parsed, flags) => {
  const errors = [];
  const warnings = [];
  const rows = Array.isArray(parsed && parsed.data) ? parsed.data.filter(row =>
    Array.isArray(row) && row.some(cell => value(cell))) : [];
  if (!rows.length) return { records: [], errors: [issue(null, 'The CSV must include a header row and at least one data row.')], warnings };
  const header = rows[0].map(value);
  if (header.length !== FLAG_IMPORT_HEADERS.length ||
      header.some((item, index) => item.toLowerCase() !== FLAG_IMPORT_HEADERS[index])) {
    errors.push(issue(1, 'Headers must be user_id,flag_code,flag_name in that exact order.'));
  }
  const dataRows = rows.slice(1);
  if (!dataRows.length) errors.push(issue(null, 'The CSV must include at least one data row.'));
  if (dataRows.length > FLAG_IMPORT_MAX_ROWS) errors.push(issue(null, `The CSV can contain at most ${FLAG_IMPORT_MAX_ROWS} data rows.`));
  const records = dataRows.map((row, index) => {
    const rowNumber = index + 2;
    if (row.length !== 3) errors.push(issue(rowNumber, 'Each record must contain exactly three columns.'));
    const record = { rowNumber, memberId: value(row[0]), code: code(row[1]), name: value(row[2]) };
    if (!record.memberId) errors.push(issue(rowNumber, 'user_id is required.'));
    else if (record.memberId.length > 200) errors.push(issue(rowNumber, 'user_id must be at most 200 characters.'));
    if (!record.code) errors.push(issue(rowNumber, 'flag_code is required.'));
    else if (record.code.length > 64) errors.push(issue(rowNumber, 'flag_code must be at most 64 characters.'));
    if (!record.name) errors.push(issue(rowNumber, 'flag_name is required.'));
    else if (record.name.length > 200) errors.push(issue(rowNumber, 'flag_name must be at most 200 characters.'));
    return record;
  });
  (parsed && Array.isArray(parsed.errors) ? parsed.errors : []).forEach(error =>
    errors.push(issue(Number.isInteger(error.row) ? error.row + 1 : null, error.message || 'The CSV could not be parsed.')));

  if (!Array.isArray(flags)) {
    warnings.push('The current flag list is unavailable, so new-code name consistency cannot be checked. The server will validate all rows.');
  } else {
    const existingCodes = new Set(flags.map(flag => code(flag.code)).filter(Boolean));
    const newCodeNames = new Map();
    records.forEach(record => {
      if (existingCodes.has(record.code)) return;
      const previous = newCodeNames.get(record.code);
      if (previous && previous.name !== record.name) {
        errors.push(issue(record.rowNumber, `New flag code ${record.code} has conflicting provisional names in this file.`));
      } else newCodeNames.set(record.code, record);
    });
  }
  const pairs = new Set();
  records.forEach(record => {
    const pair = `${record.memberId}\u0000${record.code}`;
    if (pairs.has(pair)) warnings.push(`Record ${record.rowNumber} repeats member ${record.memberId} and code ${record.code}; existing active assignments are skipped by the server.`);
    pairs.add(pair);
  });
  return { records, errors, warnings };
};
